// Swiper is imported already
(async () => {

  /**
   * Main Product
   */
  if (!customElements.get('main-product')) {
    class MainProduct extends HTMLElement {
      constructor() {
        super();

        // Cache for prefetched data
        this.prefetchCache = new Map();

        // Initialize prefetching
        this.initPrefetching();

        // Handle browser back/forward navigation
        this.handlePopstate = this.handlePopstate.bind(this);
        window.addEventListener('popstate', this.handlePopstate);

        this.addEventListener('click', (event) => {
          if (event.target.closest('[data-main-product-variant]')) {
            event.preventDefault();
            const url = event.target.closest('[data-main-product-variant]').href;

            // if the url is not the same as the current url, update the product
            if (window.location.href.split('/').at(-1) !== url.split('/').at(-1)) {
              this.handleColorVariant(url);
            }
          }
        });

        // Lazy-load Yotpo Reviews on first open of the Reviews tab
        this.initReviewsLazyLoad();
      }

      handlePopstate(event) {
        // Handle browser back/forward navigation
        const currentUrl = window.location.href;

        // Check if we have cached data for the current URL
        if (this.prefetchCache.has(currentUrl)) {
          const cachedData = this.prefetchCache.get(currentUrl);
          this.updateProductContent(cachedData, currentUrl, false); // Don't push state again
          return;
        }

        // If not cached, fetch the content
        fetch(currentUrl)
          .then((response) => response.text())
          .then((responseText) => {
            // Cache the response for future use
            this.prefetchCache.set(currentUrl, responseText);
            this.updateProductContent(responseText, currentUrl, false); // Don't push state again
          })
          .catch((error) => {
            console.error('Failed to load product content on navigation:', error);
            // Fallback: reload the page
            window.location.reload();
          });
      }

      handleColorVariant(url) {
        // Check if we have cached data first
        if (this.prefetchCache.has(url)) {
          const cachedData = this.prefetchCache.get(url);
          this.updateProductContent(cachedData, url);
          return;
        }

        fetch(url)
          .then((response) => response.text())
          .then((responseText) => {
            // Cache the response for future use
            this.prefetchCache.set(url, responseText);
            this.updateProductContent(responseText, url);
          });
      }

      updateProductContent(responseText, url, shouldPushState = true) {
        // Replace the section html with the new html
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        this.innerHTML = html.querySelector('main-product').innerHTML;
        // update the url to the new url (only if not handling popstate)
        if (shouldPushState) {
          window.history.pushState({}, '', url);
        }

        // Update product recommendations section
        this.updateProductRecommendations(html);

        // Update sticky-ATC section
        // Try to find it in the current HTML first, otherwise fetch it
        this.updateStickyATC(url, html);

        try {
          if (window.yotpoWidgetsContainer?.initWidgets) {
            window.yotpoWidgetsContainer.initWidgets();
          } else if (window.yotpo?.refreshWidgets) {
            window.yotpo.refreshWidgets();
          }
        } catch (e) {
          console.error('Failed to initialize Yotpo widgets:', e);
        }

        // Re-initialize Klaviyo back-in-stock functionality
        this.reinitializeKlaviyoBIS();

        // Track product view for e-commerce analytics
        //this.trackProductView(url);

        // Re-initialize prefetching for new content
        this.initPrefetching();

        // Re-bind Yotpo Reviews lazy-load after DOM replacement
        this.initReviewsLazyLoad();

        // Re-bind complete look triggers after content update
        this.bindCompleteLookTriggers();

      }

      initReviewsLazyLoad() {
        // Delegate click on the reviews collapsible trigger to inject widget markup on first open
        // Builds the Yotpo widget HTML from data attributes and injects into [data-yotpo-reviews-mount]
        if (this.__reviewsLazyInit) return;
        this.__reviewsLazyInit = true;

        this.addEventListener(
          'click',
          (e) => {
            const trigger = e.target && e.target.closest && e.target.closest('.collapsible__trigger[data-yotpo-reviews-container]');
            if (!trigger) return;

            const root = trigger.closest('.product__collapsible');
            if (!root) return;

            const mount = root.querySelector('[data-yotpo-reviews-mount]');
            if (!mount || mount.__yotpoLoaded) return;
            mount.__yotpoLoaded = true;

            const instanceId = document.querySelector('meta[name="yotpo-reviews-instance-id"]')?.content || '';
            const productId = (window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.product && window.ShopifyAnalytics.meta.product.id) || '';
            const name = document.querySelector('h1')?.textContent?.trim() || '';
            const url = window.location.origin + window.location.pathname;
            const mainImage = this.querySelector('img[srcset], img[src]');
            const imageUrl = (mainImage && (mainImage.currentSrc || mainImage.src)) || '';
            const priceText = (this.querySelector('.price .price__current, .product__price .price__current, [data-price]')?.textContent || '').replace(/[^\d.,]/g, '');
            const price = priceText ? (parseFloat(priceText.replace(',', '.')) || '') : '';
            const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || '';
            const description = '';

            const widgetHtml = `
              <div class="yotpo-widget-instance"
                   data-yotpo-instance-id="${instanceId}"
                   data-yotpo-product-id="${productId}"
                   data-yotpo-name="${name}"
                   data-yotpo-url="${url}"
                   data-yotpo-image-url="${imageUrl}"
                   data-yotpo-price="${price}"
                   data-yotpo-currency="${currency}"
                   data-yotpo-description="${description}"
                   style="min-height: 20px;"></div>`;

            mount.innerHTML = widgetHtml;

            // Robust Yotpo init with retries for timing issues
            (function initYotpoWithRetry(attemptsLeft) {
              const tryInit = () => {
                try {
                  if (window.yotpoWidgetsContainer?.initWidgets) {
                    window.yotpoWidgetsContainer.initWidgets();
                    return true;
                  }
                  if (window.yotpo && typeof window.yotpo.refreshWidgets === 'function') {
                    window.yotpo.refreshWidgets();
                    return true;
                  }
                } catch (e) {
                  console.error('Failed to initialize Yotpo widgets:', e);
                }
                return false;
              };

              if (tryInit()) return;
              if (attemptsLeft <= 0) return;
              setTimeout(() => initYotpoWithRetry(attemptsLeft - 1), 200);
            })(15); // ~3s total
          },
          true
        );
      }

      updateProductRecommendations(html) {
        // Find the product-recommendations section in the new HTML
        const newRecommendationsSection = html.querySelector('product-recommendations');
        const currentRecommendationsSection = document.querySelector('product-recommendations');

        if (newRecommendationsSection && currentRecommendationsSection) {
          // Get the new data-url from the new HTML
          const newDataUrl = newRecommendationsSection.dataset.url;

          if (newDataUrl) {
            // Fetch new recommendations using the new URL
            fetch(newDataUrl)
              .then((response) => response.text())
              .then((text) => {
                const newHtml = new DOMParser()
                  .parseFromString(text, 'text/html')
                  .querySelector('[data-recommended-products]').innerHTML;

                const currentRecommendedProducts = currentRecommendationsSection.querySelector('[data-recommended-products]');

                if (currentRecommendedProducts) {
                  // Update the content with fresh recommendations
                  currentRecommendedProducts.innerHTML = newHtml;

                  // Update the swiper instance if it exists
                  if (currentRecommendationsSection.swiperInstance) {
                    currentRecommendationsSection.swiperInstance.update();

                    // Re-initialize navigation, pagination, and scrollbar if they exist
                    if (currentRecommendationsSection.swiperInstance.navigation) {
                      currentRecommendationsSection.swiperInstance.navigation.init();
                      currentRecommendationsSection.swiperInstance.navigation.update();
                    }
                    if (currentRecommendationsSection.swiperInstance.pagination) {
                      currentRecommendationsSection.swiperInstance.pagination.init();
                      currentRecommendationsSection.swiperInstance.pagination.update();
                    }
                    if (currentRecommendationsSection.swiperInstance.scrollbar) {
                      currentRecommendationsSection.swiperInstance.scrollbar.init();
                      currentRecommendationsSection.swiperInstance.scrollbar.updateSize();
                    }
                  }
                }
              })
              .catch((error) => {
                console.error('Failed to fetch new recommendations:', error);
              });
          }
        }
      }

      updateStickyATC(url, html) {
        // Find the current sticky-ATC section container
        const currentStickyATC = document.querySelector('product-sticky-atc');
        if (!currentStickyATC) return;

        // First, try to find the sticky-ATC in the provided HTML (from the full page response)
        const newStickyATCInHtml = html?.querySelector('product-sticky-atc');
        if (newStickyATCInHtml) {
          // Find the section wrapper in the new HTML
          const newStickyATCSection = newStickyATCInHtml.closest('.shopify-section') || newStickyATCInHtml.closest('[id^="shopify-section-"]');
          const currentStickyATCSection = currentStickyATC.closest('.shopify-section') || currentStickyATC.closest('[id^="shopify-section-"]');

          if (newStickyATCSection && currentStickyATCSection) {
            // Replace the section content
            currentStickyATCSection.innerHTML = newStickyATCSection.innerHTML;

            // Re-initialize the ProductStickyAtc component
            this.reinitializeStickyATC(currentStickyATCSection);
            return;
          }
        }

        // If not found in the HTML, fetch it separately using section_id
        const stickyATCSection = currentStickyATC.closest('.shopify-section') || currentStickyATC.closest('[id^="shopify-section-"]');
        if (!stickyATCSection) return;

        // Try to get section ID from data-id attribute or ID
        let sectionId = stickyATCSection.dataset.id || stickyATCSection.id?.replace('shopify-section-', '');

        // If still no section ID, try to find it in the URL pattern or use a default
        if (!sectionId) {
          // Try to extract from the section's data attributes or fallback to 'sticky-atc'
          sectionId = stickyATCSection.querySelector('[data-section-id]')?.dataset.sectionId || 'sticky-atc';
        }

        if (sectionId) {
          this.fetchAndUpdateStickyATC(url, sectionId, stickyATCSection);
        }
      }

      fetchAndUpdateStickyATC(url, sectionId, sectionElement) {
        // Fetch the updated sticky-ATC section
        fetch(`${url}?section_id=${sectionId}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');

            // Find the new sticky-ATC section in the response
            // Shopify sections are typically wrapped in a div with id="shopify-section-{sectionId}"
            const sectionIdAttr = `shopify-section-${sectionId}`;
            const newStickyATCSection = html.getElementById(sectionIdAttr) || html.querySelector(`[id*="${sectionId}"]`);

            if (newStickyATCSection) {
              // Replace the section content
              sectionElement.innerHTML = newStickyATCSection.innerHTML;

              // Re-initialize the ProductStickyAtc component
              this.reinitializeStickyATC(sectionElement);
            }
          })
          .catch((error) => {
            console.error('Failed to fetch new sticky-ATC section:', error);
          });
      }

      reinitializeStickyATC(sectionElement) {
        // Re-initialize the ProductStickyAtc component by cloning
        // This ensures the constructor runs again and event listeners are re-bound
        const newProductStickyATC = sectionElement.querySelector('product-sticky-atc');
        if (newProductStickyATC && customElements.get('product-sticky-atc')) {
          // Clone and replace to trigger re-initialization
          const cloned = newProductStickyATC.cloneNode(true);
          newProductStickyATC.replaceWith(cloned);
        }
      }


      trackProductView(url) {
        // Track product view for e-commerce analytics
        try {
          console.log('Tracking product view for:', url);

          // Extract product ID from URL
          const productId = this.extractProductIdFromUrl(url);
          const productTitle = document.querySelector('h1')?.textContent || 'Product';

          // Method 1: Google Tag Manager - Enhanced Ecommerce
          if (typeof window.dataLayer !== 'undefined') {
            window.dataLayer.push({
              'event': 'view_item',
              'ecommerce': {
                'currency': window.Shopify?.currency?.active || 'USD',
                'value': this.getProductPrice(),
                'items': [{
                  'item_id': productId,
                  'item_name': productTitle,
                  'item_category': this.getProductCategory(),
                  'price': this.getProductPrice(),
                  'quantity': 1
                }]
              }
            });
            console.log('GTM product view tracked');
          }

        } catch (error) {
          console.warn('Error tracking product view:', error);
        }
      }

      extractProductIdFromUrl(url) {
        // Extract product ID from URL
        try {
          const urlObj = new URL(url);
          const pathParts = urlObj.pathname.split('/');
          const productIndex = pathParts.indexOf('products');
          if (productIndex !== -1 && pathParts[productIndex + 1]) {
            return pathParts[productIndex + 1];
          }
          return null;
        } catch (error) {
          console.warn('Error extracting product ID:', error);
          return null;
        }
      }

      getProductPrice() {
        // Get current product price
        try {
          const priceElement = document.querySelector('.price .price__current, .product__price .price__current, [data-price]');
          if (priceElement) {
            const priceText = priceElement.textContent.replace(/[^\d.,]/g, '');
            return parseFloat(priceText.replace(',', '.')) || 0;
          }
          return 0;
        } catch (error) {
          console.warn('Error getting product price:', error);
          return 0;
        }
      }

      getProductCategory() {
        // Get product category/collection
        try {
          const breadcrumb = document.querySelector('.breadcrumb a:last-child');
          if (breadcrumb) {
            return breadcrumb.textContent.trim();
          }
          return 'Products';
        } catch (error) {
          console.warn('Error getting product category:', error);
          return 'Products';
        }
      }

      reinitializeKlaviyoBIS() {
        // Disable original Klaviyo and use custom handlers
        setTimeout(() => {
          const buttons = this.querySelectorAll('.klaviyo-bis-trigger');
          if (buttons.length > 0) {
            // Disable original Klaviyo to prevent conflicts
            if (window.klaviyo?.disable) {
              try {
                window.klaviyo.disable("backinstock");
              } catch (e) {
                console.log('Could not disable Klaviyo');
              }
            }

            // Remove any existing Klaviyo event listeners
            buttons.forEach(btn => {
              const newBtn = btn.cloneNode(true);
              btn.parentNode.replaceChild(newBtn, btn);

              // Remove any data attributes that might trigger Klaviyo
              newBtn.removeAttribute('data-klaviyo-trigger');
              newBtn.removeAttribute('data-klaviyo-bis-trigger');

              newBtn.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                e.stopImmediatePropagation();
                this.showKlaviyoModal();
              });
            });
          }
        }, 100);
      }

      showKlaviyoModal(variantIdOverride = null) {
        // Remove any existing modal first
        const existingModal = document.getElementById('klaviyo-modal');
        if (existingModal) {
          existingModal.remove();
        }

        const variantId = variantIdOverride || this.querySelector('input[name="id"]')?.value;
        const productTitle = document.querySelector('h1')?.textContent || 'Product';
        const translations = window.klaviyoStrings || {
          body_content: "Register to receive a notification when this item comes back in stock.",
          email_field_label: "Email",
          button_label: "Notify me when available",
          newsletter_subscribe_label: "Add me",
          subscription_success_label: "You're in! We'll let you know when it's back."
        };

        // Get unavailable variants for dropdown
        const unavailableVariants = this.getUnavailableVariants(variantId);

        const modal = document.createElement('div');
        modal.id = 'klaviyo-modal';
        modal.innerHTML = `
          <div style="position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,0.65);z-index:999999;display:flex;align-items:center;justify-content:center;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:14px;line-height:1.5;color:#222;">
            <div style="background:#fff;padding:30px 40px;border-radius:3px;max-width:460px;width:90%;position:relative;margin-top:100px;box-shadow:0 4px 20px rgba(0,0,0,0.3);">
              <button id="close-modal" style="position:absolute;top:7px;right:15px;background:none;border:none;font-size:30px;font-weight:700;line-height:1;color:#ccc;cursor:pointer;text-shadow:0 1px 0 #fff;">&times;</button>
              <h3 style="margin:0 0 20px 0;font-size:24px;line-height:1.5;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-weight:500;color:inherit;">${productTitle}</h3>
              <p style="margin-bottom:20px;">${translations.body_content}</p>
              <form id="klaviyo-form">
                ${unavailableVariants.length > 1 ? `
                  <div style="margin-bottom:15px;">
                    <label style="display:block;margin-bottom:5px;font-weight:bold;">Variant</label>
                    <select id="klaviyo-variant" style="display:block;width:100%;height:46px;padding:10px 16px;font-size:14px;line-height:1.3333333;color:#222;background-color:#fff;background-image:none;border:1px solid #ccc;border-radius:2px;box-shadow:inset 0 1px 1px rgba(0,0,0,.075);transition:border-color ease-in-out .15s,box-shadow ease-in-out .15s;box-sizing:border-box;">
                      ${unavailableVariants.map(variant =>
                        `<option value="${variant.id}" ${variant.id == variantId ? 'selected' : ''}>${variant.title}</option>`
                      ).join('')}
                    </select>
                  </div>
                ` : ''}
                <div style="margin-bottom:15px;">
                  <label style="display:block;margin-bottom:5px;font-weight:bold;">${translations.email_field_label}</label>
                  <input type="email" id="klaviyo-email" required style="display:block;width:100%;height:46px;padding:10px 16px;font-size:14px;line-height:1.3333333;color:#222;background-color:#fff;background-image:none;border:1px solid #ccc;border-radius:2px;box-shadow:inset 0 1px 1px rgba(0,0,0,.075);transition:border-color ease-in-out .15s,box-shadow ease-in-out .15s;box-sizing:border-box;">
                  <div id="klaviyo-error" style="display:none;padding:6px 11px;font-size:13px;margin:15px 0;border:1px solid transparent;border-radius:2px;color:#C72E2F;background-color:#fcd6d7;border-color:#fcd6d7;"></div>
                </div>
                <div style="margin-bottom:15px;">
                  <p style="margin:0;">
                    <input type="checkbox" id="newsletter" checked style="margin-right:10px;">
                    <label for="newsletter" style="font-size:13px;opacity:0.835;">${translations.newsletter_subscribe_label}</label>
                  </p>
                </div>
                <div style="margin-top:20px;">
                  <button type="submit" style="display:inline-block;padding:14px;margin-bottom:0;font-size:15px;font-weight:bold;line-height:1.3333333;text-align:center;white-space:nowrap;vertical-align:middle;cursor:pointer;user-select:none;background-image:none;border:1px solid transparent;border-radius:2px;width:100%;color:#fff;background-color:#000;border-color:#000;">${translations.button_label}</button>
                </div>
              </form>
              <div id="klaviyo-success" style="display:none;padding:6px 11px;font-size:13px;margin:15px 0;border:1px solid transparent;border-radius:2px;color:#1B9500;background-color:#d3efcd;border-color:#d3efcd;text-align:center;">
                ${translations.subscription_success_label} <a href="#" id="close-success" style="color:#1B9500;text-decoration:underline;">Close</a>
              </div>
            </div>
          </div>
        `;

        document.body.appendChild(modal);

        // Event listeners
        document.getElementById('close-modal').onclick = () => modal.remove();
        document.getElementById('close-success').onclick = (e) => { e.preventDefault(); modal.remove(); };
        document.getElementById('klaviyo-form').onsubmit = (e) => {
          e.preventDefault();
          const selectedVariantId = document.getElementById('klaviyo-variant')?.value || variantId;
          this.submitKlaviyo(document.getElementById('klaviyo-email').value, selectedVariantId);
        };

        setTimeout(() => document.getElementById('klaviyo-email').focus(), 100);
      }

      async submitKlaviyo(email, variantId) {
        try {
          // Clear any existing errors
          const errorDiv = document.getElementById('klaviyo-error');
          const successDiv = document.getElementById('klaviyo-success');
          if (errorDiv) errorDiv.style.display = 'none';
          if (successDiv) successDiv.style.display = 'none';

          const { default: klaviyo } = await import('./modules/klaviyo.js');
          await klaviyo.createClientBackInStockSubscription({ email, variantId });

          // Show success message (append, don't replace form)
          const successDivElement = document.getElementById('klaviyo-success');
          if (successDivElement) {
            successDivElement.style.display = 'block';
          }
        } catch (error) {
          console.error('Klaviyo submission failed:', error);

          // Show error below email field
          const errorDiv = document.getElementById('klaviyo-error');
          if (errorDiv) {
            errorDiv.textContent = error.message || 'Subscription failed. Please try again.';
            errorDiv.style.display = 'block';
          }
        }
      }

      getUnavailableVariants(targetVariantId = null) {
        // Get unavailable variants for the dropdown
        try {
          // Try to get variant data from the existing script
          const variantDataScript = this.querySelector('script[type="application/json"]');
          if (variantDataScript) {
            const productData = JSON.parse(variantDataScript.textContent);
            if (Array.isArray(productData)) {
              // Filter for unavailable variants
              let unavailableVariants = productData.filter(variant => !variant.available || variant.inventory_quantity <= 0);

              // If a target variant ID is provided and it's not in the list, add it
              if (targetVariantId) {
                const targetVariant = productData.find(v => String(v.id) === String(targetVariantId));
                if (targetVariant && !unavailableVariants.find(v => String(v.id) === String(targetVariantId))) {
                  unavailableVariants.push(targetVariant);
                }
              }

              return unavailableVariants;
            }
          }

          // Fallback: return target variant or current variant as unavailable
          const variantIdToUse = targetVariantId || this.querySelector('input[name="id"]')?.value;
          if (variantIdToUse) {
            return [{
              id: variantIdToUse,
              title: 'Selected Variant',
              available: false,
              inventory_quantity: 0
            }];
          }

          return [];
        } catch (error) {
          console.warn('Error getting unavailable variants:', error);
          return [];
        }
      }

      initPrefetching() {
        // Get all variant links
        const variantLinks = this.querySelectorAll('[data-main-product-variant]');

        if (variantLinks.length === 0) return;

        // Set up intersection observer for prefetching when links come into view
        const observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              const link = entry.target;
              this.prefetchVariant(link.href);
              // Stop observing once prefetched
              observer.unobserve(link);
            }
          });
        }, {
          rootMargin: '50px' // Start prefetching 50px before the link comes into view
        });

        // Variant hover overlay: one shared hide-delay so moving between swatches doesn’t flicker
        const overlay = this.querySelector('[data-variant-hover-overlay]');
        const overlayImg = this.querySelector('[data-variant-hover-overlay-image]');
        const OVERLAY_HIDE_DELAY_MS = 120;
        const overlayVisibleClass = 'product-media-slider__variant-hover-overlay--visible';
        let overlayHideTimeout = null;

        function showVariantOverlay(imageUrl) {
          if (!overlay || !overlayImg) return;
          if (overlayHideTimeout) {
            clearTimeout(overlayHideTimeout);
            overlayHideTimeout = null;
          }
          if (overlayImg.src !== imageUrl) overlayImg.src = imageUrl;
          overlay.classList.add(overlayVisibleClass);
        }

        function scheduleHideVariantOverlay() {
          if (!overlay) return;
          if (overlayHideTimeout) clearTimeout(overlayHideTimeout);
          overlayHideTimeout = setTimeout(() => {
            overlayHideTimeout = null;
            if (overlay.isConnected) overlay.classList.remove(overlayVisibleClass);
          }, OVERLAY_HIDE_DELAY_MS);
        }

        variantLinks.forEach(link => {
          link.addEventListener('mouseenter', () => {
            this.prefetchVariant(link.href);
          }, { once: true });

          observer.observe(link);

          const isCurrent = link.getAttribute('data-variant-current') === 'true';
          if (isCurrent) return;

          const imageUrl = link.dataset.variantFeaturedImage;
          if (!imageUrl) return;

          link.addEventListener('mouseenter', () => showVariantOverlay(imageUrl));
          link.addEventListener('mouseleave', () => scheduleHideVariantOverlay());
        });
      }

      prefetchVariant(url) {
        // Skip if already cached or currently loading
        if (this.prefetchCache.has(url) || this.prefetchCache.has(`${url}_loading`)) {
          return;
        }

        // Mark as loading to prevent duplicate requests
        this.prefetchCache.set(`${url}_loading`, true);

        fetch(url, {
          method: 'GET',
          headers: {
            'X-Requested-With': 'XMLHttpRequest'
          }
        })
          .then(response => {
            if (response.ok) {
              return response.text();
            }
            throw new Error('Network response was not ok');
          })
          .then(responseText => {
            // Cache the successful response
            this.prefetchCache.set(url, responseText);
            // Remove loading marker
            this.prefetchCache.delete(`${url}_loading`);
          })
          .catch(error => {
            console.warn('Prefetch failed for:', url, error);
            // Remove loading marker on error
            this.prefetchCache.delete(`${url}_loading`);
          });
      }

      bindCompleteLookTriggers() {
        // Clone elements to remove existing event listeners
        let completeLookTriggers = document.querySelectorAll('[data-quickshop]');
        let addToCartTriggers = document.querySelectorAll('[data-quickshop-add-to-cart]');

        completeLookTriggers.forEach((trigger) => {
          const clonedTrigger = trigger.cloneNode(true);
          trigger.replaceWith(clonedTrigger);
        });

        addToCartTriggers.forEach((trigger) => {
          const clonedTrigger = trigger.cloneNode(true);
          trigger.replaceWith(clonedTrigger);
        });

        // Get fresh references to the cloned elements
        let freshCompleteLookTriggers = document.querySelectorAll('[data-quickshop]');
        let freshAddToCartTriggers = document.querySelectorAll('[data-quickshop-add-to-cart]');

        // Create bound handlers
        this.completeLookHandler = (event) => {
          let variantId = event.target.value;
          if (variantId) {
            this.addUpsell(variantId);
          }
        };

        this.addToCartHandler = (event) => {
          let variantId = event.target.dataset.variantId;
          if (variantId) {
            this.addUpsell(variantId);
          }
        };

        // Add new listeners to cloned elements
        freshCompleteLookTriggers.forEach((trigger) => {
          trigger.addEventListener('change', this.completeLookHandler);
        });

        freshAddToCartTriggers.forEach((trigger) => {
          trigger.addEventListener('click', this.addToCartHandler);
        });
      }

      addUpsell(variantId) {
        let cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer) {
          cartDrawer.addUpsell(variantId);
        }
      }

      disconnectedCallback() {
        // Clean up event listener when component is removed
        window.removeEventListener('popstate', this.handlePopstate);
      }
    }

    customElements.define('main-product', MainProduct);
  }


  /**
   * Product Detail Swiper
   */
  if (!customElements.get('product-detail-swiper')) {
    class ProductDetailSwiper extends HTMLElement {
      constructor() {
        super();

        this.swiperElement = this.querySelector('[data-product-detail-swiper]');

        this.swiperOptions = {
          direction: 'horizontal',
        };

        this.init();
      }

      init() {
        window.addEventListener('load', (event) => {
          this.swiperInstance = new Swiper(this.swiperElement, this.swiperOptions);
        });
      }
    }

    customElements.define('product-detail-swiper', ProductDetailSwiper);
  }


  /**
   * Product Sticky Atc
   */
  if (!customElements.get('product-sticky-atc')) {
    class ProductStickyAtc extends HTMLElement {
      constructor() {
        super();

        // Use event delegation for better performance and reliability
        this.addEventListener('click', this.handleVariantClick.bind(this));

        this.trackAtc();
      }

      trackAtc() {
        let atc = document.querySelector('[data-track-add-to-cart]');
        let offsetTop = atc.offsetTop;
        let offsetBottom = offsetTop + window.innerHeight + atc.clientHeight;

        if (window.innerWidth < 768) {
          if (window.innerHeight < offsetTop) {
            document.querySelector('product-sticky-atc').classList.add('hidden');
          } else {
            document.querySelector('product-sticky-atc').classList.remove('hidden');
          }
        }

        window.addEventListener(
          'scroll',
          () => {
            let currentScroll = window.innerHeight + window.scrollY;
            if (currentScroll > offsetTop) {
              if (currentScroll > offsetBottom) {
                document.querySelector('product-sticky-atc').classList.remove('hidden');
                if (window.innerWidth < 768) {
                  document.body.style.paddingBottom = '87.33px';
                } else {
                  document.body.style.paddingBottom = '0';
                }
              } else {
                document.querySelector('product-sticky-atc').classList.add('hidden');
              }
            } else {
              document.querySelector('product-sticky-atc').classList.add('hidden');
            }
          },
          false
        );
      }

      handleVariantClick(event) {
        // Ignore clicks on buttons, links, and other interactive elements
        if (event.target.closest('button, a, toggle-element-class')) return;

        // Find the closest variant element with the ID
        const variantElement = event.target.closest('[data-sticky-atc-variant]');
        if (!variantElement) return;

        // Get variant ID from the element
        const variantId = variantElement.dataset.stickyAtcVariantId;
        if (!variantId || variantId === '') return;

        // Check if this variant is disabled/out of stock
        // The variant-disabled class is added to out-of-stock variants
        const isOutOfStock = variantElement.classList.contains('variant-disabled');

        // Close the variant selector
        document.body.classList.remove('variant-selector-open');

        if (isOutOfStock) {
          this.handleOutOfStock(variantId);
        } else {
          this.handleAddToCart(variantId);
        }
      }

      handleAddToCart(variantId) {
        // Update the variant ID in the hidden input
        const variantInput = document.querySelector('form[action*="/cart/add"] input[name="id"]');
        if (!variantInput) {
          console.warn('Could not find variant input');
          return;
        }

        variantInput.value = variantId;
        variantInput.dispatchEvent(new Event('change', { bubbles: true }));

        // Update visual variant selector if it exists
        this.updateVariantSelector(variantId);

        // Trigger add to cart after a short delay to ensure variant is updated
        setTimeout(() => {
          const addToCartButton = document.querySelector('[data-track-add-to-cart]');
          if (addToCartButton) {
            addToCartButton.click();
          }
        }, 150);
      }

      handleOutOfStock(variantId) {
        const mainProduct = document.querySelector('main-product');
        if (mainProduct && typeof mainProduct.showKlaviyoModal === 'function') {
          mainProduct.showKlaviyoModal(variantId);
        } else {
          // Fallback: update variant input and click trigger button
          const variantInput = document.querySelector('form[action*="/cart/add"] input[name="id"]');
          if (variantInput) {
            variantInput.value = variantId;
            variantInput.dispatchEvent(new Event('change', { bubbles: true }));
          }

          setTimeout(() => {
            const triggerButton = document.querySelector('.klaviyo-bis-trigger');
            if (triggerButton) {
              triggerButton.click();
            }
          }, 150);
        }
      }

      updateVariantSelector(variantId) {
        const variantRadios = document.querySelector('variant-radios');
        if (!variantRadios) return;

        const variantDataScript = variantRadios.querySelector('script[type="application/json"]');
        if (!variantDataScript) return;

        try {
          const variants = JSON.parse(variantDataScript.textContent);
          const selectedVariant = variants.find(v => String(v.id) === String(variantId));

          if (selectedVariant && selectedVariant.options) {
            // Update radio inputs to match the selected variant
            selectedVariant.options.forEach((optionValue, index) => {
              const radioInput = variantRadios.querySelector(
                `[data-option-group="${index}"] input[value="${CSS.escape(optionValue)}"]`
              );
              if (radioInput && !radioInput.checked) {
                radioInput.click();
              }
            });
          }
        } catch (e) {
          console.warn('Could not update variant selector:', e);
        }
      }
    }

    customElements.define('product-sticky-atc', ProductStickyAtc);
  }


  /**
   * Product Info
   */
  if (!customElements.get('product-info')) {
    class ProductInfo extends HTMLElement {
      constructor() {
        super();
        this.productForm = this.querySelector('product-form');
        this.productFormQuantity = this.productForm?.querySelector('input[name="quantity"]');
        this.quantityInput = this.querySelector('quantity-input');
        this.quantityInputElement = this.quantityInput?.querySelector('input[name="quantity"]');
        this.quantityInputElement?.addEventListener('change', () => {
          this.handleQuantityInput();
        });

        let descriptionTrigger = this.querySelector('[data-product-description-trigger]');
        let productDescription = this.querySelector('[data-product-tabs-description]');
        if (descriptionTrigger && productDescription) {
          descriptionTrigger.addEventListener('click', (event) => {
            productDescription.classList.add('collapsible-is-open');
            productDescription.scrollIntoView({ behavior: 'smooth', block: 'center' });
          });
        }
      }

      /**
       * handleQuantityInput
       * @description Set the quantity from the quantity input web component to the product form web component.
       */
      handleQuantityInput() {
        this.productFormQuantity.value = this.quantityInputElement.value;
      }
    }
    customElements.define('product-info', ProductInfo);
  }
})();
